import { cn } from '@/shared/lib/utils';

/*
 * Display only. The backend writes each formula as text lines — "name = symbols",
 * "= the same with values", "= result" — and this file lays them out: every
 * value is shown above the name it stands for. Nothing here evaluates anything;
 * when a formula's two lines do not have the same shape it falls back to the text.
 */

type Token =
  { kind: 'word'; text: string } | { kind: 'number'; text: string } | { kind: 'op'; text: string };

const TOKEN = /\d+(?:\.\d+)?%?|[A-Za-z][A-Za-z ]*[A-Za-z]|[A-Za-z]|[()+\-−×÷,]/g;

function tokenize(expression: string): Token[] {
  return (expression.match(TOKEN) ?? []).map((text) =>
    /^\d/.test(text)
      ? { kind: 'number', text }
      : /^[A-Za-z]/.test(text)
        ? { kind: 'word', text }
        : { kind: 'op', text },
  );
}

type Term =
  | { kind: 'operand'; value: string; name: string | null }
  | { kind: 'function'; text: string }
  | { kind: 'op'; text: string };

/** Pairs the symbolic line with the substituted one, term by term; null when their shapes differ. */
function pair(symbols: string, values: string): Term[] | null {
  const named = tokenize(symbols);
  const worked = tokenize(values);
  if (named.length === 0 || named.length !== worked.length) return null;

  const terms: Term[] = [];
  for (let i = 0; i < named.length; i += 1) {
    const s = named[i] as Token;
    const v = worked[i] as Token;
    if (s.kind === 'op' && v.kind === 'op' && s.text === v.text) terms.push(s);
    else if (s.kind === 'word' && v.kind === 'word' && s.text === v.text)
      terms.push({ kind: 'function', text: s.text });
    else if (s.kind === 'number' && v.kind === 'number' && s.text === v.text)
      terms.push({ kind: 'operand', value: v.text, name: null });
    else if (s.kind === 'word' && v.kind === 'number')
      terms.push({ kind: 'operand', value: v.text, name: s.text });
    else return null;
  }
  return terms;
}

interface Group {
  /** "Price after gateway fee" — what the formula produces. */
  name: string;
  /** The right-hand side in words; empty when the line is a plain statement. */
  symbols: string;
  /** The "= …" lines under it, in order. */
  lines: string[];
}

/** Splits the backend's lines into one group per named formula. */
function toGroups(lines: string[]): Group[] {
  const groups: Group[] = [];
  for (const line of lines) {
    const current = groups.at(-1);
    if (line.startsWith('=') && current) {
      current.lines.push(line.replace(/^=\s*/, ''));
      continue;
    }
    const at = line.indexOf(' = ');
    groups.push(
      at === -1
        ? { name: line, symbols: '', lines: [] }
        : { name: line.slice(0, at), symbols: line.slice(at + 3), lines: [] },
    );
  }
  return groups;
}

function Equation({ terms }: { terms: Term[] }) {
  return (
    <div className="flex flex-wrap items-start gap-x-1 gap-y-2">
      {terms.map((term, index) => {
        if (term.kind === 'op') {
          const bracket = term.text === '(' || term.text === ')';
          return (
            <span
              key={index}
              className={cn(
                'text-muted-foreground pt-0.5 leading-5',
                bracket ? 'text-base font-light' : 'px-0.5 text-sm',
              )}
            >
              {term.text === ',' ? ',' : term.text}
            </span>
          );
        }
        if (term.kind === 'function')
          return (
            <span key={index} className="text-muted-foreground pt-1 text-xs italic">
              {term.text}
            </span>
          );
        return (
          <span key={index} className="inline-flex flex-col items-center">
            <span
              className={cn(
                'rounded px-1.5 text-sm leading-6 font-semibold tabular-nums',
                term.name && 'bg-muted',
              )}
            >
              {term.value}
            </span>
            {term.name && (
              <span className="text-muted-foreground mt-0.5 max-w-28 text-center text-[10px] leading-tight">
                {term.name}
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}

function FormulaGroup({ group }: { group: Group }) {
  const substitution = group.lines.length > 1 ? group.lines[0] : undefined;
  const result = group.lines.at(-1);
  const between = group.lines.slice(1, -1);
  const terms = substitution !== undefined ? pair(group.symbols, substitution) : null;

  // A plain statement ("Provider cost = USD 9.90") or a formula with no values.
  if (!result)
    return (
      <p className="text-sm">
        <span className="text-muted-foreground">{group.name}</span>
        {group.symbols && (
          <>
            {' = '}
            <span className="font-semibold tabular-nums">{group.symbols}</span>
          </>
        )}
      </p>
    );

  return (
    <div className="flex flex-col gap-2">
      <p className="text-muted-foreground text-[11px]">
        <span className="text-foreground font-medium">{group.name}</span>
        {!terms && group.symbols && <> = {group.symbols}</>}
      </p>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-col gap-1">
          {terms ? (
            <Equation terms={terms} />
          ) : (
            substitution !== undefined && (
              <p className="text-sm tabular-nums">
                <span className="text-muted-foreground">=</span> {substitution}
              </p>
            )
          )}
          {between.map((line, index) => (
            <p key={index} className="text-muted-foreground text-xs tabular-nums">
              <span>=</span> <span>{line}</span>
            </p>
          ))}
        </div>
        <p className="border-border bg-background flex shrink-0 items-baseline gap-1.5 rounded-md border px-2.5 py-1">
          <span className="text-muted-foreground text-xs">=</span>
          <span className="text-sm font-semibold tabular-nums">{result}</span>
        </p>
      </div>
    </div>
  );
}

/**
 * A formula as the backend worked it, laid out as an equation: each value with
 * the name of what it is underneath, then the result. The original lines stay
 * available to screen readers (and to copy) as text.
 */
export function WorkedFormula({ lines, label }: { lines: string[]; label?: string }) {
  if (lines.length === 0) return null;
  const groups = toGroups(lines);
  return (
    <div aria-label={label} className="bg-muted/40 mt-2 rounded-md px-3 py-2.5">
      <div aria-hidden className="divide-border flex flex-col divide-y">
        {groups.map((group, index) => (
          <div key={index} className="py-2 first:pt-0 last:pb-0">
            <FormulaGroup group={group} />
          </div>
        ))}
      </div>
      <div className="sr-only">
        {lines.map((line, index) => (
          <p key={index}>{line}</p>
        ))}
      </div>
    </div>
  );
}
